import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, RotateCcw, RefreshCw, AlertTriangle } from "lucide-react";

/**
 * Phase J — Agency Identity card.
 *
 * Lives below the existing Agency branding card on the Settings page and
 * drives the onboarding / intelligence / approval flow:
 *
 *   draft → analyse → needs_review → (user edits) → approved
 *
 * Every AI-generated field is editable; nothing is auto-locked.
 */
export default function AgencyIdentityPanel({ agency, setAgency }) {
    const [analyzing, setAnalyzing] = useState(false);
    const [busy, setBusy] = useState(false);
    const status = (agency?.identity_status || "draft").toLowerCase();
    const conf = agency?.confidence || {};
    const src = agency?.source_tag || {};

    // -- Manual onboarding inputs (socials, location, services, etc.) --
    const updateField = (k, v) => setAgency(a => ({ ...(a || {}), [k]: v }));

    const saveManual = async () => {
        setBusy(true);
        try {
            const payload = {
                instagram: agency?.instagram || "",
                linkedin: agency?.linkedin || "",
                youtube: agency?.youtube || "",
                facebook: agency?.facebook || "",
                location: agency?.location || "",
                services: agency?.services || [],
                preferred_language: agency?.preferred_language || "",
                target_clients: agency?.target_clients || "",
            };
            const { data } = await api.put("/agency", payload);
            setAgency(data);
            toast.success("Onboarding inputs saved");
        } catch (e) {
            toast.error(`Save failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const saveIdentity = async () => {
        setBusy(true);
        try {
            const payload = {
                summary: agency?.summary || "",
                positioning: agency?.positioning || "",
                tone_of_voice: agency?.tone_of_voice || "",
                creative_style: agency?.creative_style || "",
                target_market: agency?.target_market || "",
                personality: agency?.personality || [],
                strengths: agency?.strengths || [],
                brand_colors: agency?.brand_colors || [],
                typography_direction: agency?.typography_direction || "",
            };
            const { data } = await api.put("/agency", payload);
            setAgency(data);
            toast.success("Identity draft saved");
        } catch (e) {
            toast.error(`Save failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const analyze = async () => {
        if (!(agency?.agency_name || "").trim()) {
            toast.error("Set your agency name first.");
            return;
        }
        setAnalyzing(true);
        try {
            const { data } = await api.post("/agency/analyze");
            setAgency(data.agency);
            if (data.scraped_ok) toast.success("Analysis complete — review the draft below");
            else toast.message("Analysis complete", { description: "Website couldn't be read — draft built from your inputs + logo only." });
        } catch (e) {
            toast.error(`Analysis failed: ${e.userMessage || e.message}`);
        } finally { setAnalyzing(false); }
    };

    const approve = async () => {
        setBusy(true);
        try {
            // Persist any in-flight edits first so the approved identity matches what the user sees.
            await saveIdentity();
            const { data } = await api.post("/agency/identity/approve");
            setAgency(data);
            toast.success("Identity approved — CEO Agent now speaks as your agency");
        } catch (e) {
            toast.error(`Approve failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const reset = async () => {
        if (!window.confirm("Reset AI-analysed identity fields? Your manual inputs (logo, website, socials) will be preserved.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/agency/identity/reset");
            setAgency(data);
            toast.success("Identity reset to draft");
        } catch (e) {
            toast.error(`Reset failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const servicesString = Array.isArray(agency?.services) ? agency.services.join(", ") : "";
    const setServices = (s) => updateField("services",
        s.split(",").map(x => x.trim()).filter(Boolean));
    const personalityString = Array.isArray(agency?.personality) ? agency.personality.join(", ") : "";
    const setPersonality = (s) => updateField("personality",
        s.split(",").map(x => x.trim()).filter(Boolean));
    const strengthsString = Array.isArray(agency?.strengths) ? agency.strengths.join(", ") : "";
    const setStrengths = (s) => updateField("strengths",
        s.split(",").map(x => x.trim()).filter(Boolean));

    return (
        <div className="card-base p-6 mb-6" data-testid="agency-identity-card">
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                    <div className="label-micro mb-1">Agency Identity</div>
                    <div className="text-sm text-zinc-400 max-w-2xl">
                        Teach the CEO Agent who your agency is, how you speak, and what you sell.
                        Once approved, every CEO response and branded export will reflect this identity.
                    </div>
                </div>
                <StatusBadge status={status} />
            </div>

            {/* 1. ONBOARDING INPUTS */}
            <div className="space-y-4 border-t border-zinc-900 pt-5">
                <div className="label-micro text-zinc-500">Step 1 — Who you are</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Location">
                        <input data-testid="agency-location-input"
                            className={INPUT_CLS}
                            placeholder="City, Country"
                            value={agency?.location || ""}
                            onChange={e => updateField("location", e.target.value)} />
                    </Field>
                    <Field label="Preferred language">
                        <input data-testid="agency-language-input"
                            className={INPUT_CLS}
                            placeholder="English, Hindi, …"
                            value={agency?.preferred_language || ""}
                            onChange={e => updateField("preferred_language", e.target.value)} />
                    </Field>
                </div>
                <Field label="Services offered (comma-separated)">
                    <input data-testid="agency-services-input"
                        className={INPUT_CLS}
                        placeholder="Brand Films, AI Ad Production, Social Content"
                        value={servicesString}
                        onChange={e => setServices(e.target.value)} />
                </Field>
                <Field label="Target clients">
                    <textarea data-testid="agency-target-clients-input"
                        className={INPUT_CLS + " min-h-[64px] resize-y"}
                        placeholder="D2C brands, launch campaigns, real estate…"
                        value={agency?.target_clients || ""}
                        onChange={e => updateField("target_clients", e.target.value)} />
                </Field>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Instagram">
                        <input data-testid="agency-instagram-input"
                            className={INPUT_CLS}
                            placeholder="@handle or full URL"
                            value={agency?.instagram || ""}
                            onChange={e => updateField("instagram", e.target.value)} />
                    </Field>
                    <Field label="LinkedIn">
                        <input data-testid="agency-linkedin-input"
                            className={INPUT_CLS}
                            placeholder="linkedin.com/company/…"
                            value={agency?.linkedin || ""}
                            onChange={e => updateField("linkedin", e.target.value)} />
                    </Field>
                    <Field label="YouTube">
                        <input data-testid="agency-youtube-input"
                            className={INPUT_CLS}
                            placeholder="@channel or URL"
                            value={agency?.youtube || ""}
                            onChange={e => updateField("youtube", e.target.value)} />
                    </Field>
                    <Field label="Facebook">
                        <input data-testid="agency-facebook-input"
                            className={INPUT_CLS}
                            placeholder="facebook.com/… (optional)"
                            value={agency?.facebook || ""}
                            onChange={e => updateField("facebook", e.target.value)} />
                    </Field>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                    <button data-testid="agency-onboarding-save-btn"
                        onClick={saveManual}
                        disabled={busy}
                        className="px-4 py-2 border border-zinc-800 rounded bg-zinc-950 hover:bg-zinc-900 text-sm disabled:opacity-50">
                        {busy ? "Saving…" : "Save onboarding inputs"}
                    </button>
                    <button data-testid="agency-analyze-btn"
                        onClick={analyze}
                        disabled={analyzing || busy}
                        className="px-4 py-2 bg-[#FF4400] hover:bg-[#ff5a1f] text-white text-sm font-medium rounded disabled:opacity-50 inline-flex items-center gap-2">
                        <Sparkles className="w-4 h-4" />
                        {analyzing ? "Analyzing…" : "Analyze My Agency"}
                    </button>
                </div>
            </div>

            {/* 2. AI DRAFT / EDIT ZONE — shown once analysed OR already approved */}
            {(status === "needs_review" || status === "approved") && (
                <div className="space-y-4 border-t border-zinc-900 mt-6 pt-5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="label-micro text-zinc-500">Step 2 — What AI understood (everything editable)</div>
                        {agency?.last_analyzed_at && (
                            <div className="text-[10px] font-mono text-zinc-600">
                                last analyzed · {new Date(agency.last_analyzed_at).toLocaleString()}
                            </div>
                        )}
                    </div>

                    <IdField label="Summary" field="summary" conf={conf} src={src}>
                        <textarea data-testid="agency-summary-input"
                            className={INPUT_CLS + " min-h-[70px] resize-y"}
                            value={agency?.summary || ""}
                            onChange={e => updateField("summary", e.target.value)} />
                    </IdField>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <IdField label="Positioning" field="positioning" conf={conf} src={src}>
                            <input data-testid="agency-positioning-input"
                                className={INPUT_CLS}
                                value={agency?.positioning || ""}
                                onChange={e => updateField("positioning", e.target.value)} />
                        </IdField>
                        <IdField label="Target market" field="target_market" conf={conf} src={src}>
                            <input data-testid="agency-target-market-input"
                                className={INPUT_CLS}
                                value={agency?.target_market || ""}
                                onChange={e => updateField("target_market", e.target.value)} />
                        </IdField>
                        <IdField label="Tone of voice" field="tone_of_voice" conf={conf} src={src}>
                            <input data-testid="agency-tone-input"
                                className={INPUT_CLS}
                                placeholder="e.g. calm, confident, technical"
                                value={agency?.tone_of_voice || ""}
                                onChange={e => updateField("tone_of_voice", e.target.value)} />
                        </IdField>
                        <IdField label="Creative style" field="creative_style" conf={conf} src={src}>
                            <input data-testid="agency-creative-style-input"
                                className={INPUT_CLS}
                                placeholder="e.g. cinematic minimal"
                                value={agency?.creative_style || ""}
                                onChange={e => updateField("creative_style", e.target.value)} />
                        </IdField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <IdField label="Personality (comma-separated)" field="personality" conf={conf} src={src}>
                            <input data-testid="agency-personality-input"
                                className={INPUT_CLS}
                                value={personalityString}
                                onChange={e => setPersonality(e.target.value)} />
                        </IdField>
                        <IdField label="Strengths (comma-separated)" field="strengths" conf={conf} src={src}>
                            <input data-testid="agency-strengths-input"
                                className={INPUT_CLS}
                                value={strengthsString}
                                onChange={e => setStrengths(e.target.value)} />
                        </IdField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <IdField label="Brand colors" field="brand_colors" conf={conf} src={src}>
                            <BrandColorsEditor
                                colors={agency?.brand_colors || []}
                                onChange={(c) => updateField("brand_colors", c)}
                            />
                        </IdField>
                        <IdField label="Typography direction" field="typography_direction" conf={conf} src={src}>
                            <select data-testid="agency-typography-input"
                                className={INPUT_CLS}
                                value={agency?.typography_direction || "unknown"}
                                onChange={e => updateField("typography_direction", e.target.value)}>
                                {["unknown", "serif", "sans-serif", "geometric sans", "script", "display"].map(v => (
                                    <option key={v} value={v}>{v}</option>
                                ))}
                            </select>
                        </IdField>
                    </div>

                    {(agency?.missing_info || []).length > 0 && (
                        <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
                            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-amber-400 mb-2">
                                <AlertTriangle className="w-3.5 h-3.5" /> Needs confirmation
                            </div>
                            <ul className="text-xs text-zinc-300 list-disc pl-5 space-y-1">
                                {agency.missing_info.map((m, i) => <li key={i}>{m}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap pt-2">
                        <button data-testid="agency-identity-save-btn"
                            onClick={saveIdentity}
                            disabled={busy}
                            className="px-4 py-2 border border-zinc-800 rounded bg-zinc-950 hover:bg-zinc-900 text-sm disabled:opacity-50">
                            Save edits
                        </button>
                        <button data-testid="agency-reanalyze-btn"
                            onClick={analyze}
                            disabled={analyzing || busy}
                            className="px-4 py-2 border border-zinc-800 rounded bg-zinc-950 hover:bg-zinc-900 text-sm inline-flex items-center gap-2 disabled:opacity-50">
                            <RefreshCw className="w-3.5 h-3.5" /> Re-analyze
                        </button>
                        {status !== "approved" ? (
                            <button data-testid="agency-approve-btn"
                                onClick={approve}
                                disabled={busy}
                                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded disabled:opacity-50 inline-flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4" /> Approve identity
                            </button>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 font-mono uppercase tracking-wider">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Active — injected into CEO Agent
                            </span>
                        )}
                        <button data-testid="agency-reset-btn"
                            onClick={reset}
                            disabled={busy}
                            className="px-3 py-2 border border-zinc-800 rounded bg-zinc-950 hover:bg-red-950/40 text-xs inline-flex items-center gap-2 text-red-300 disabled:opacity-50">
                            <RotateCcw className="w-3.5 h-3.5" /> Reset
                        </button>
                    </div>
                </div>
            )}

            {status === "draft" && (
                <div className="mt-5 text-[11px] text-zinc-500 font-mono uppercase tracking-wider">
                    tip: save your inputs above, then click "Analyze My Agency" to generate an editable draft.
                </div>
            )}
        </div>
    );
}

/* ----- small presentational helpers ----- */

const INPUT_CLS = "w-full px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-orange-500/60";

function Field({ label, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function IdField({ label, field, conf, src, children }) {
    const c = (conf?.[field] || "").toLowerCase();
    const s = (src?.[field] || "").toLowerCase();
    const confColor = c === "high" ? "text-emerald-400"
        : c === "medium" ? "text-amber-400"
        : c ? "text-red-400" : "text-zinc-600";
    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs uppercase tracking-wider text-zinc-500">{label}</span>
                <span className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
                    {s && <span className="text-zinc-600">src:{s}</span>}
                    {c && <span className={confColor}>conf:{c}</span>}
                </span>
            </div>
            {children}
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        draft: { cls: "border-zinc-800 text-zinc-500", label: "DRAFT" },
        needs_review: { cls: "border-amber-500/40 text-amber-400", label: "NEEDS REVIEW" },
        approved: { cls: "border-emerald-500/50 text-emerald-400", label: "APPROVED" },
    };
    const m = map[status] || map.draft;
    return (
        <span data-testid="agency-identity-status"
            className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider border rounded-sm ${m.cls}`}>
            {m.label}
        </span>
    );
}

function BrandColorsEditor({ colors, onChange }) {
    const add = () => onChange([...(colors || []), "#FF4400"]);
    const update = (i, v) => {
        const next = [...colors]; next[i] = v; onChange(next);
    };
    const remove = (i) => onChange(colors.filter((_, idx) => idx !== i));
    return (
        <div className="flex flex-wrap gap-2 items-center">
            {(colors || []).map((c, i) => (
                <div key={i} className="inline-flex items-center gap-1 border border-zinc-800 rounded-sm bg-zinc-950 pl-1 pr-2">
                    <input type="color" value={c || "#FF4400"}
                        onChange={e => update(i, e.target.value)}
                        className="w-6 h-6 bg-transparent border-0 cursor-pointer" />
                    <span className="font-mono text-[11px] text-zinc-300">{c}</span>
                    <button onClick={() => remove(i)}
                        className="ml-1 text-zinc-500 hover:text-red-400 text-xs" aria-label="remove">×</button>
                </div>
            ))}
            <button onClick={add}
                className="px-2 py-1 text-xs border border-zinc-800 rounded-sm bg-zinc-950 hover:bg-zinc-900 text-zinc-300">
                + Add
            </button>
        </div>
    );
}
